import { IEventBusListenerData, IEventBusPlugin } from '../core/types';

import Vue from 'vue';

export interface IEventBusStore {
  listeners: Record<string, number>;
  listenerHistory: Array<IEventBusListenerData & { action: 'attached' | 'detached' }>;
  currentListeners: Map<string, IEventBusListenerData>;
}

export class EventBusStateManagerPlugin implements IEventBusPlugin {
  id = 'event-bus-state-manager';
  name = 'Event Bus State Manager';
  enabled = true;

  private store: IEventBusStore;
  private readonly MAX_HISTORY_LENGTH = 100;

  constructor() {
    this.store = Vue.observable<IEventBusStore>({
      listeners: {},
      listenerHistory: [],
      currentListeners: new Map()
    });
  }

  onListenerAttached(data: IEventBusListenerData): void {
    const eventName = data.eventName;
    // Deep clone the data
    const clonedData = { ...data };

    // Create new Map for currentListeners
    const newListeners = new Map(this.store.currentListeners);
    newListeners.set(this.generateListenerKey(clonedData), clonedData);
    this.store.currentListeners = newListeners;

    // Clone listeners object
    const newListenerCounts = { ...this.store.listeners };
    newListenerCounts[eventName] = (newListenerCounts[eventName] || 0) + 1;
    this.store.listeners = newListenerCounts;

    // Clone history array
    this.store.listenerHistory = [
      { ...clonedData, action: 'attached' as const },
      ...this.store.listenerHistory
    ].slice(0, this.MAX_HISTORY_LENGTH);
  }

  onListenerDetached(data: IEventBusListenerData): void {
    const eventName = data.eventName;
    const clonedData = { ...data };

    const newListeners = new Map(this.store.currentListeners);
    newListeners.delete(this.generateListenerKey(clonedData));
    this.store.currentListeners = newListeners;

    const newListenerCounts = { ...this.store.listeners };
    if (newListenerCounts[eventName]) {
      if (clonedData.removeEvent) {
        newListenerCounts[eventName] = 0;
      } else {
        newListenerCounts[eventName] = Math.max(0, newListenerCounts[eventName] - 1);
      }

      if (newListenerCounts[eventName] === 0) {
        delete newListenerCounts[eventName];
      }
    }
    this.store.listeners = newListenerCounts;

    this.store.listenerHistory = [
      { ...clonedData, action: 'detached' as const },
      ...this.store.listenerHistory
    ].slice(0, this.MAX_HISTORY_LENGTH);
  }

  // Generate a unique key for each listener
  private generateListenerKey(data: IEventBusListenerData): string {
    // Use a combination of eventName, componentName and the function reference
    // Convert the function to string to include in the key
    // const fnString = data.listenerFn.toString();
    // Use a hash of the function string to keep the key shorter
    // const fnHash = this.simpleHash(fnString);
    // :${fnHash}
    return `${data.eventName}:${data.componentName || 'anonymous'}`;
  }

  // Simple hash function for strings
  private simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  getStore(): IEventBusStore {
    return this.store;
  }

  getActiveListenersCount(): number {
    return this.store.currentListeners.size;
  }
}
